import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import useTrainingSocket from '../hooks/training/useTrainingSocket';
import trainingApi from '../services/trainingApi';

const TrainingContext = createContext(null);

export const useTraining = () => useContext(TrainingContext);

/**
 * TrainingProvider wraps all /training/* routes.
 * Provides: socket connection, user info, notifications, API helpers.
 */
export const TrainingProvider = ({ children, token, user }) => {
  const { socket, isConnected, joinSession, leaveSession } = useTrainingSocket(token);
  const [notifications, setNotifications] = useState([]);

  // Join user notification room on connect
  useEffect(() => {
    if (!socket || !isConnected || !user?._id) return;

    socket.emit('training:join_user_room', { userId: user._id });

    const handleFeedbackReleased = (data) => {
      setNotifications(prev => [{
        id: Date.now(),
        type: 'feedback_released',
        message: `Feedback available for ${data.scenarioName || 'your session'}`,
        sessionId: data.sessionId,
        createdAt: new Date().toISOString(),
        read: false,
      }, ...prev]);
    };

    const handleAssignmentNew = (data) => {
      setNotifications(prev => [{
        id: Date.now(),
        type: 'assignment_new',
        message: `New assignment: ${data.scenarioName || 'Training scenario'}`,
        assignmentId: data.assignmentId,
        createdAt: new Date().toISOString(),
        read: false,
      }, ...prev]);
    };

    socket.on('training:feedback_released', handleFeedbackReleased);
    socket.on('training:assignment_new', handleAssignmentNew);

    return () => {
      socket.off('training:feedback_released', handleFeedbackReleased);
      socket.off('training:assignment_new', handleAssignmentNew);
    };
  }, [socket, isConnected, user?._id]);

  const markNotificationRead = useCallback((notificationId) => {
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  const value = {
    socket,
    isConnected,
    joinSession,
    leaveSession,
    user,
    token,
    notifications,
    unreadCount,
    markNotificationRead,
    trainingApi,
  };

  return (
    <TrainingContext.Provider value={value}>
      {children}
    </TrainingContext.Provider>
  );
};

export default TrainingContext;
